import { ORDER_MODES } from '../constants/appConstants';

/** Above this available ct, clients see a capped label instead of the exact amount. */
export const CLIENT_LOT_AVAILABLE_CAP_CT = 10;

/**
 * Measurements suffix for client lot cards, e.g. `5.8x5mm`.
 * @param {object|null|undefined} lot
 * @returns {string}
 */
export function formatLotMeasurementsMm(lot) {
  if (!lot) return '';
  if (lot.length != null && lot.width != null) {
    return `${lot.length}x${lot.width}mm`;
  }
  const raw = lot.measurement && String(lot.measurement).trim();
  if (!raw) return '';
  const withoutMm = raw.replace(/\s*mm\s*$/i, '').trim();
  return withoutMm ? `${withoutMm}mm` : '';
}

/**
 * Client-facing lot title: `{Shape} {Measurements}mm ({caratPerStone}ct)`.
 * @param {object|null|undefined} lot
 * @returns {string}
 */
export function getNonCertLotClientCardTitle(lot) {
  if (!lot) return '—';
  const shape = String(lot.shape || '').trim();
  const mm = formatLotMeasurementsMm(lot);
  const cps = lot.caratPerStone;
  const hasCt = cps != null && cps !== '' && !Number.isNaN(Number(cps));
  const ctPart = hasCt
    ? (mm ? `(${Number(cps)}ct)` : `${Number(cps)}ct`)
    : '';
  const head = [shape, mm].filter(Boolean).join(' ');
  if (head && ctPart) return `${head} ${ctPart}`;
  return head || ctPart || '—';
}

/**
 * Client-facing available label; caps display when stock exceeds configured max.
 * @param {number|string|null|undefined} availableCarat
 * @param {{ maxAvailableDisplayCt?: number }|null|undefined} settings
 * @returns {string}
 */
export function formatLotAvailableForClient(availableCarat, settings = null) {
  const n = Number(availableCarat);
  if (Number.isNaN(n) || n <= 0) return '—';
  const cap = settings?.maxAvailableDisplayCt ?? CLIENT_LOT_AVAILABLE_CAP_CT;
  if (n > cap) return `${cap}ct+`;
  return `${n} ct`;
}

/**
 * Human-readable label for a non-certified inventory lot (matches Inventory-style shape + size).
 * @param {object|null|undefined} lot - populated NonCertifiedDiamond
 * @returns {string}
 */
export function getNonCertLotDisplayTitle(lot) {
  if (!lot) return 'Non-certified lot';
  const shape = String(lot.shape || '').toUpperCase();
  let dims = '';
  if (lot.length != null && lot.width != null) {
    dims = `${lot.length}×${lot.width} mm`;
  } else if (lot.measurement && String(lot.measurement).trim()) {
    dims = String(lot.measurement).trim();
  }
  const head = [shape, dims].filter(Boolean).join(' ').trim();
  if (head) return head;
  const cps = lot.caratPerStone;
  if (cps != null && Number(cps) > 0) {
    return `${shape || 'Lot'} · up to ${Number(cps)} ct/stone`.trim();
  }
  if (shape) return shape;
  return 'Non-certified lot';
}

/**
 * One-line label for cart / check rows: lot title + reserved quantity.
 * For by-count items shows stone count with estimated weight; for by-weight shows carats.
 * @param {object} item - cart or order item with `lot`, optional `weight`, `stoneCount`, `orderMode`.
 * @returns {string}
 */
export function getNonCertOrderItemDisplayLabel(item) {
  const title = getNonCertLotDisplayTitle(item?.lot);
  if (item?.orderMode === ORDER_MODES.BY_COUNT && item?.stoneCount > 0) {
    const suffix = item?.weight != null ? ` | ${item.stoneCount} stones (~${item.weight} ct)` : ` | ${item.stoneCount} stones`;
    return `${title}${suffix}`;
  }
  const raw = item?.weight;
  if (raw != null && raw !== '' && !Number.isNaN(Number(raw))) {
    return `${title} | ${Number(raw)} ct`;
  }
  return title;
}
